import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@repo/ui'
import { Link } from '@tanstack/react-router'
import { ExternalLink, Wrench } from 'lucide-react'

import type {
  McpAgentToolBinding,
  McpConnection,
  McpTool,
  McpToolConfirmation,
} from '@/types/mcp'

interface McpTabProps {
  connections: McpConnection[]
  toolsByConnection: Record<string, McpTool[]>
  bindings: McpAgentToolBinding[]
  isLoading: boolean
  isUpdating: boolean
  onBindingChange: (
    toolId: string,
    patch: { enabled?: boolean; confirmation?: McpToolConfirmation }
  ) => void
}

export function McpTab({
  connections,
  toolsByConnection,
  bindings,
  isLoading,
  isUpdating,
  onBindingChange,
}: McpTabProps) {
  const bindingByToolId = new Map(
    bindings.map((binding) => [binding.toolId, binding])
  )

  return (
    <div className="flex flex-col gap-6">
      <section>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold">Ferramentas MCP</h3>
            <p className="text-muted-foreground mt-1 text-xs">
              Defina quais ferramentas externas este agente pode consultar ou
              executar.
            </p>
          </div>
          <Button asChild variant="outline" size="sm" className="h-8 gap-1">
            <Link to="/integracoes/mcp">
              <ExternalLink className="size-3.5" />
              Conexões
            </Link>
          </Button>
        </div>

        <div className="border-border bg-card mt-4 overflow-hidden rounded-lg border">
          {isLoading ? (
            <div className="text-muted-foreground p-4 text-sm">
              Carregando ferramentas MCP...
            </div>
          ) : connections.length === 0 ? (
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="bg-muted flex size-8 shrink-0 items-center justify-center rounded-md">
                  <Wrench className="text-muted-foreground size-4" />
                </div>
                <div>
                  <p className="text-sm font-medium">
                    Nenhuma conexão MCP cadastrada
                  </p>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Cadastre um servidor MCP em Integrações para liberar
                    ferramentas externas por agente.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            connections.map((connection) => {
              const tools = toolsByConnection[connection.id] ?? []
              return (
                <div
                  key={connection.id}
                  className="border-border border-b last:border-0"
                >
                  <div className="bg-muted/30 flex flex-wrap items-center gap-2 px-4 py-3">
                    <p className="text-sm font-medium">{connection.name}</p>
                    <ConnectionStatusBadge status={connection.status} />
                    <span className="text-muted-foreground text-xs">
                      {tools.length} ferramenta(s)
                    </span>
                  </div>
                  {tools.length === 0 ? (
                    <p className="text-muted-foreground px-4 py-3 text-xs">
                      Sincronize esta conexão em Integrações para listar
                      ferramentas.
                    </p>
                  ) : (
                    tools.map((tool) => {
                      const binding = bindingByToolId.get(tool.id)
                      const enabled = binding?.enabled ?? false
                      const confirmation =
                        binding?.confirmation ??
                        (tool.isSensitive ? 'HUMAN_APPROVAL' : 'NONE')
                      const disabled =
                        isUpdating || connection.status !== 'ACTIVE'

                      return (
                        <div
                          key={tool.id}
                          className="border-border flex flex-col gap-3 border-t p-4 sm:flex-row sm:items-center"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-medium">
                                {tool.title || tool.name}
                              </p>
                              {tool.isSensitive ? (
                                <Badge variant="secondary" className="text-2xs">
                                  sensível
                                </Badge>
                              ) : null}
                            </div>
                            {tool.description ? (
                              <p className="text-muted-foreground mt-1 text-xs">
                                {tool.description}
                              </p>
                            ) : null}
                          </div>
                          <Select
                            value={confirmation}
                            disabled={disabled || !enabled}
                            onValueChange={(value) =>
                              onBindingChange(tool.id, {
                                confirmation: value as McpToolConfirmation,
                              })
                            }
                          >
                            <SelectTrigger className="w-full sm:w-48">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="NONE">
                                Sem confirmação
                              </SelectItem>
                              <SelectItem value="HUMAN_APPROVAL">
                                Aprovação da equipe
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <Switch
                            checked={enabled}
                            disabled={disabled}
                            onCheckedChange={(checked) =>
                              onBindingChange(tool.id, {
                                enabled: checked,
                                confirmation,
                              })
                            }
                          />
                        </div>
                      )
                    })
                  )}
                </div>
              )
            })
          )}
        </div>
        <p className="text-muted-foreground mt-3 text-xs">
          Ferramentas MCP só entram no prompt quando a conexão está ativa e a
          ferramenta está habilitada para este agente.
        </p>
      </section>
    </div>
  )
}

function ConnectionStatusBadge({
  status,
}: {
  status: McpConnection['status']
}) {
  if (status === 'ACTIVE') {
    return <Badge className="bg-success/15 text-success">Ativa</Badge>
  }
  if (status === 'ERROR') {
    return <Badge variant="destructive">Erro</Badge>
  }
  return <Badge variant="secondary">Desativada</Badge>
}
